import PlatformIcon from '@/components/creator/PlatformIcon';
import { Box, chakra, Span } from '@chakra-ui/react';
import { NavArrowRight } from 'iconoir-react';
import { forwardRef } from 'react';
import { CONTACT_NAV_ICONS, type ContactNavIconName } from './contact-nav-icons';

/**
 * Icon shown in the row's leading tile. Either a platform brand glyph rendered
 * white on its brand-colour tile (Profils rows) or a neutral iconoir glyph on a
 * `whiteSmoke` tile (Collaborations rows).
 */
export type ContactNavIcon =
  | { kind: 'platform'; platform: string; brandColor: string }
  | { kind: 'glyph'; name: ContactNavIconName };

export interface ContactNavRowProps {
  icon: ContactNavIcon;
  title: string;
  /** One-line status under the title, e.g. "84,2K abonnés · 4,8% ER". */
  sublabel?: string;
  /** Drives `aria-current` + selected styling; idle when false. */
  selected?: boolean;
  /** "YouTube · bientôt" coming-soon row: muted, aria-disabled, skipped. */
  disabled?: boolean;
  /** Roving-tabindex: only the active row in the list is focusable (0), rest -1. */
  tabIndex?: number;
  onSelect?: () => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
}

const ICON_TILE = '28px';
const PLATFORM_TILE = '23px';

/**
 * THE reused rail atom: icon tile + title + sublabel + chevron, with
 * selected / idle / disabled states. One data-driven component backs every rail
 * row (IG, TikTok, Affiliation, UGC, Gifting, Ads, and the disabled YouTube
 * coming-soon row).
 *
 * Rendered as a `listitem` inside a roving-tabindex `list` (see the parent
 * lists). Idle rows are white cards on the cream rail; the selected row takes a
 * charcoal hairline on a whiteSmoke fill — border + bg contrast only, no shadow,
 * per house rules. Disabled (coming-soon) rows wear a dashed hairline, a faded
 * tile and a plain label, and are removed from the tab order.
 */
const ContactNavRow = forwardRef<HTMLElement, ContactNavRowProps>(
  (
    { icon, title, sublabel, selected = false, disabled = false, tabIndex, onSelect, onKeyDown },
    ref
  ) => {
    const isGlyph = icon.kind === 'glyph';
    const GlyphIcon = icon.kind === 'glyph' ? CONTACT_NAV_ICONS[icon.name] : null;

    /**
     * Disabled (coming-soon) rows render a non-interactive `chakra.div`; active
     * rows render a `chakra.button`. The shared `HTMLElement` ref forwards to
     * either. `type` is button-only, so it is set on the interactive branch.
     */
    const Element = disabled ? chakra.div : chakra.button;

    return (
      <Element
        ref={ref}
        role="listitem"
        type={disabled ? undefined : 'button'}
        aria-current={selected ? 'true' : undefined}
        aria-disabled={disabled ? 'true' : undefined}
        tabIndex={disabled ? undefined : tabIndex}
        onClick={disabled ? undefined : onSelect}
        onKeyDown={disabled ? undefined : onKeyDown}
        display="flex"
        alignItems="center"
        gap="2.5"
        width="full"
        textAlign="left"
        px="11px"
        py={isGlyph ? '10px' : '9px'}
        rounded="10px"
        borderWidth="1px"
        borderStyle={disabled ? 'dashed' : 'solid'}
        borderColor={disabled ? 'border.emphasized' : selected ? 'primary.900' : 'border'}
        bg={disabled ? 'transparent' : selected ? 'whiteSmoke' : 'panelBg'}
        cursor={disabled ? 'default' : 'pointer'}
        transition="background 0.15s, border-color 0.15s"
        _hover={disabled || selected ? undefined : { bg: 'babyPowder' }}
        _focusVisible={{
          outline: '2px solid',
          outlineColor: 'primary.900',
          outlineOffset: '1px',
        }}
      >
        {icon.kind === 'platform' ? (
          <Box
            flex="none"
            boxSize={PLATFORM_TILE}
            rounded="6px"
            display="grid"
            placeItems="center"
            bg={icon.brandColor}
            color="white"
            opacity={disabled ? 0.5 : 1}
          >
            <PlatformIcon platform={icon.platform} size={14} />
          </Box>
        ) : (
          <Box
            flex="none"
            boxSize={ICON_TILE}
            rounded="lg"
            display="grid"
            placeItems="center"
            bg="whiteSmoke"
            color={selected ? 'primary.700' : 'primary.600'}
          >
            {GlyphIcon && <GlyphIcon width={15} height={15} />}
          </Box>
        )}

        <Box flex="1" minW="0">
          <Span
            display="block"
            fontFamily={disabled ? 'body' : 'heading'}
            fontSize={disabled ? '12px' : isGlyph ? '13px' : '12.5px'}
            fontWeight={disabled ? 'normal' : 'semibold'}
            color={disabled ? 'primary.400' : 'primary.900'}
            lineHeight="1.2"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {title}
          </Span>
          {sublabel && (
            <Span
              display="block"
              mt="0.5"
              lineHeight="1.2"
              fontFamily="body"
              fontSize={isGlyph ? '10.5px' : '11px'}
              color="primary.400"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {sublabel}
            </Span>
          )}
        </Box>

        {!disabled && (
          <Box asChild flex="none" color={selected ? 'primary.600' : 'primary.300'}>
            <NavArrowRight width={16} height={16} />
          </Box>
        )}
      </Element>
    );
  }
);

ContactNavRow.displayName = 'ContactNavRow';

export default ContactNavRow;
